/**
 * Lightweight metadata about each report type. Lives in `src/config/`
 * so service-layer modules (e.g. `services/dam/damStorage.ts`) can
 * import it without dragging in the much heavier
 * `services/reportPrompts.ts` (2.6k lines, exports the full
 * `getPromptConfig(reportType).usesScoring` graph). Pulling
 * `reportPrompts.ts` into the Firestore loader was the alternative;
 * this small mirror is the cheaper layering choice.
 *
 * Contract: `isNarrativeOnly` MUST agree with the existing
 * `scripts/verifyDamReadiness.ts:NARRATIVE_ONLY_TYPES` skip list AND
 * with `src/components/admin/DamDashboard.tsx:REPORT_TYPES` (which
 * excludes the same types). When a new type is added to either of
 * those, update this file too.
 *
 * NOTE — `usesScoring: false` is NECESSARY BUT NOT SUFFICIENT for
 * narrative-only. `test-fit` also carries `usesScoring: false` per
 * `reportPrompts.ts:getTestFitConfig`, but it DOES ship a DAM (see
 * `scripts/buildDam.ts:SUPPORTED_TYPES`). The two signals diverged
 * historically; the canonical "no DAM by design" set today is just
 * PSR (`public-sentiment`).
 */
import type { ReportTypeId } from './reportTypes';

/**
 * Report types that ship WITHOUT a DAM by design. The narrative is
 * generated directly from the SED + sources without a DAM scaffold,
 * so `loadCurrentDam(type)` would always fail and warn about a
 * missing composite index for a query that's never legitimately
 * needed.
 *
 * Currently:
 *   - 'public-sentiment' (PSR) — emits a SENTIMENT INDEX 0-100,
 *     which is a narrative verdict the body composes itself, not
 *     a deterministic feasibility-style category score. PSR-1
 *     intentionally skipped the DAM build pipeline; see
 *     `services/publicSentiment/publicSentimentOrchestrator.ts`.
 */
const NARRATIVE_ONLY_REPORT_TYPES: ReadonlySet<ReportTypeId> = new Set<ReportTypeId>([
  'public-sentiment',
]);

/**
 * True when the report type ships without a DAM by design (narrative-
 * only). Callers (in particular `damStorage.loadCurrentDam`) use this
 * to short-circuit Firestore reads that would otherwise warn about
 * missing composite indices for queries that are never legitimately
 * needed.
 *
 * Adding a new type here means a corresponding update in
 * `scripts/verifyDamReadiness.ts:NARRATIVE_ONLY_TYPES` and the
 * `DamDashboard.REPORT_TYPES` exclusion list — they MUST stay in
 * sync.
 */
export function isNarrativeOnly(reportType: ReportTypeId): boolean {
  return NARRATIVE_ONLY_REPORT_TYPES.has(reportType);
}
